import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, 
  Mail, 
  Bell, 
  Save, 
  RotateCcw,
  Eye,
  CheckCircle,
  AlertTriangle,
  CreditCard,
  Calendar,
  XCircle,
  Info,
  Copy,
  Loader2,
  Unlock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const TEMPLATE_TYPES = [
  { 
    id: 'booking_confirmation', 
    name: 'Booking Confirmation', 
    description: 'Sent after a booking is created',
    icon: CheckCircle,
    color: 'green'
  },
  { 
    id: 'booking_reminder', 
    name: 'Booking Reminder', 
    description: 'Sent 24 hours before appointment',
    icon: Bell,
    color: 'blue'
  },
  { 
    id: 'booking_cancellation', 
    name: 'Booking Cancellation', 
    description: 'Sent when a booking is cancelled',
    icon: XCircle,
    color: 'red'
  },
  { 
    id: 'payment_confirmation', 
    name: 'Payment Confirmation', 
    description: 'Sent after payment is processed',
    icon: CreditCard,
    color: 'purple'
  },
  { 
    id: 'appointment_limit_warning', 
    name: 'Appointment Limit Warning', 
    description: 'Sent when approaching or reaching appointment limit',
    icon: AlertTriangle,
    color: 'yellow'
  },
  { 
    id: 'slot_release', 
    name: 'Slot Release', 
    description: 'Sent when an on-hold slot is released',
    icon: Unlock,
    color: 'teal'
  },
];

const ManageNotificationTemplates = () => {
  const { token, getAuthHeaders, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState({});
  const [selectedType, setSelectedType] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);

  useEffect(() => {
    if (!isSuperAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchTemplates();
    fetchVariables();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/notification-templates`, {
        headers: getAuthHeaders()
      });
      setTemplates(response.data.data || []);
    } catch (err) {
      setError('Failed to load notification templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchVariables = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/notification-templates/variables`, {
        headers: getAuthHeaders()
      });
      setVariables(response.data.data || {});
    } catch (err) {
      console.error('Failed to load variables:', err);
    }
  };

  const selectTemplate = (templateType) => {
    const template = templates.find(t => t.template_type === templateType);
    setSelectedType(templateType);
    setEditingTemplate(template ? { ...template } : {
      template_type: templateType,
      subject: '',
      body: '',
      is_active: true,
      channels: ['email']
    });
    setShowPreview(false);
    setPreviewContent(null);
  };

  const handleSave = async () => {
    if (!editingTemplate) return;
    
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await axios.put(
        `${API_BASE_URL}/notification-templates/${selectedType}`,
        {
          subject: editingTemplate.subject,
          body: editingTemplate.body,
          is_active: editingTemplate.is_active,
          channels: editingTemplate.channels
        },
        { headers: getAuthHeaders() }
      );
      
      setSuccess('Template saved successfully!');
      fetchTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedType) return;
    
    if (!window.confirm('Are you sure you want to reset this template to its default content?')) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/notification-templates/${selectedType}/reset`,
        {},
        { headers: getAuthHeaders() }
      );
      
      setEditingTemplate(response.data.data);
      setSuccess('Template reset to default!');
      fetchTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset template');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!editingTemplate) return;

    try {
      const response = await axios.post(
        `${API_BASE_URL}/notification-templates/preview?template_type=${selectedType}&subject=${encodeURIComponent(editingTemplate.subject)}&body=${encodeURIComponent(editingTemplate.body)}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      setPreviewContent(response.data.data);
      setShowPreview(true);
    } catch (err) {
      setError('Failed to generate preview');
    }
  };

  const insertVariable = (variable) => {
    if (!editingTemplate) return;
    
    // Insert at cursor position in body textarea
    const textarea = document.getElementById('template-body');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = editingTemplate.body;
      const newText = text.substring(0, start) + variable + text.substring(end);
      setEditingTemplate({ ...editingTemplate, body: newText });
      
      // Set cursor position after inserted variable
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    } else {
      // Append to body if no textarea focus
      setEditingTemplate({ 
        ...editingTemplate, 
        body: editingTemplate.body + variable 
      });
    }
  };

  const copyVariable = (variable) => {
    navigator.clipboard.writeText(variable);
    setSuccess(`Copied: ${variable}`);
    setTimeout(() => setSuccess(''), 2000);
  };

  const getTemplateInfo = (typeId) => {
    return TEMPLATE_TYPES.find(t => t.id === typeId);
  };

  const getColorClasses = (color) => {
    const colors = {
      green: 'bg-green-100 text-green-700 border-green-300',
      blue: 'bg-blue-100 text-blue-700 border-blue-300',
      red: 'bg-red-100 text-red-700 border-red-300',
      purple: 'bg-purple-100 text-purple-700 border-purple-300',
      yellow: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    };
    return colors[color] || colors.blue;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-200 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Notification Templates</h1>
              <p className="text-gray-600 mt-1">Configure email and SMS notification messages</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            {success}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Template List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Template Types</h2>
              <div className="space-y-2">
                {TEMPLATE_TYPES.map((type) => {
                  const Icon = type.icon;
                  const template = templates.find(t => t.template_type === type.id);
                  const isSelected = selectedType === type.id;
                  
                  return (
                    <button
                      key={type.id}
                      onClick={() => selectTemplate(type.id)}
                      className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                        isSelected 
                          ? 'border-yellow-500 bg-yellow-50' 
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${getColorClasses(type.color)}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className="font-medium text-gray-800">{type.name}</h3>
                            {template?.is_active ? (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Active</span>
                            ) : (
                              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded">Inactive</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{type.description}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Template Editor */}
          <div className="lg:col-span-2">
            {selectedType && editingTemplate ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                {/* Editor Header */}
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {(() => {
                        const info = getTemplateInfo(selectedType);
                        const Icon = info?.icon || Mail;
                        return (
                          <>
                            <div className={`p-2 rounded-lg ${getColorClasses(info?.color || 'blue')}`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div>
                              <h2 className="text-xl font-semibold text-gray-800">{info?.name}</h2>
                              <p className="text-sm text-gray-500">{info?.description}</p>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingTemplate.is_active}
                          onChange={(e) => setEditingTemplate({
                            ...editingTemplate,
                            is_active: e.target.checked
                          })}
                          className="sr-only peer"
                        />
                        <div className="relative w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                        <span className="ml-2 text-sm font-medium text-gray-700">Active</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Editor Body */}
                <div className="p-4 space-y-4">
                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Subject
                    </label>
                    <input
                      type="text"
                      value={editingTemplate.subject}
                      onChange={(e) => setEditingTemplate({
                        ...editingTemplate,
                        subject: e.target.value
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="Enter email subject..."
                    />
                  </div>

                  {/* Body */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message Body
                    </label>
                    <textarea
                      id="template-body"
                      value={editingTemplate.body}
                      onChange={(e) => setEditingTemplate({
                        ...editingTemplate,
                        body: e.target.value
                      })}
                      rows={12}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-mono text-sm"
                      placeholder="Enter message body..."
                    />
                  </div>

                  {/* Available Variables */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Info className="w-4 h-4 text-gray-500" />
                      <h3 className="text-sm font-medium text-gray-700">Available Variables</h3>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      Click to insert at cursor position, or click the copy icon to copy to clipboard.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(variables[selectedType] || []).map((v) => (
                        <div 
                          key={v.var}
                          className="inline-flex items-center bg-white border border-gray-300 rounded-md overflow-hidden"
                        >
                          <button
                            onClick={() => insertVariable(v.var)}
                            className="px-2 py-1 text-xs font-mono text-blue-600 hover:bg-blue-50 transition-colors"
                            title={v.description}
                          >
                            {v.var}
                          </button>
                          <button
                            onClick={() => copyVariable(v.var)}
                            className="px-2 py-1 border-l border-gray-300 hover:bg-gray-100 transition-colors"
                            title="Copy to clipboard"
                          >
                            <Copy className="w-3 h-3 text-gray-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <button
                      onClick={handleReset}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Reset to Default
                    </button>
                    
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handlePreview}
                        className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        Preview
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        Save Template
                      </button>
                    </div>
                  </div>
                </div>

                {/* Preview Panel */}
                {showPreview && previewContent && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-800">Preview (with sample data)</h3>
                      <button
                        onClick={() => setShowPreview(false)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                        <p className="text-sm">
                          <span className="text-gray-500">Subject:</span>{' '}
                          <span className="font-medium">{previewContent.subject}</span>
                        </p>
                      </div>
                      <div className="p-4">
                        <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                          {previewContent.body}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600">Select a Template</h3>
                <p className="text-gray-500 mt-2">Choose a template type from the list to edit its content</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ManageNotificationTemplates;
